import { motion, AnimatePresence } from 'framer-motion';
import { Clock, ShieldAlert } from 'lucide-react';
import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Service, Action, logInfo } from '../shared/utils/LogUtility';

interface SessionTimeoutModalProps {
  isVisible: boolean;
  timeRemaining: string;
  onExtendSession: () => void;
  onSignOut: () => void;
}

const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = ({
  isVisible,
  timeRemaining,
  onExtendSession,
  onSignOut,
}) => {
  const { t } = useTranslation();

  if (isVisible && timeRemaining === '0:05') {
    logInfo(Service.SYSTEM, Action.PROCESS, 'Final countdown: 5 seconds remaining');
  }

  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={onExtendSession}
          style={{ pointerEvents: 'all' }}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 6 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 6 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="w-full max-w-sm mx-4 overflow-hidden rounded-xl shadow-xl border"
            style={{
              background: 'var(--vscode-bg-elevated)',
              borderColor: 'var(--vscode-border-primary)',
              boxShadow: 'var(--vscode-shadow-lg, 0 8px 32px rgba(0,0,0,0.4))',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Title bar — dark theme friendly */}
            <div
              className="flex items-center gap-2 px-3 py-2 shrink-0 select-none"
              style={{
                background: 'var(--ggj-header-bg, var(--brand-cta-bg))',
                color: 'var(--ggj-header-text, #fff)',
              }}
            >
              <ShieldAlert className="w-4 h-4 shrink-0" style={{ opacity: 0.9 }} />
              <span className="text-sm font-semibold">{t('dialog.sessionTimeout.titleBar')}</span>
            </div>

            {/* Content — safe area at bottom for mobile */}
            <div
              className="p-4 space-y-4"
              style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
            >
              {/* Countdown */}
              <div
                className="flex items-center gap-4 rounded-lg px-4 py-3 border"
                style={{
                  background: 'var(--vscode-bg-tertiary)',
                  borderColor: 'var(--vscode-border-primary)',
                }}
              >
                <Clock
                  className="w-5 h-5 shrink-0 animate-pulse"
                  style={{ color: 'var(--vscode-charts-yellow, var(--vscode-warning))' }}
                />
                <div>
                  <motion.p
                    key={timeRemaining}
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    className="text-2xl font-bold font-mono leading-none"
                    style={{ color: 'var(--vscode-text-primary)' }}
                  >
                    {timeRemaining}
                  </motion.p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--vscode-text-tertiary)' }}>
                    {t('dialog.sessionTimeout.timeRemainingLabel')}
                  </p>
                </div>
              </div>

              {/* Message */}
              <p className="text-sm leading-relaxed" style={{ color: 'var(--vscode-text-secondary)' }}>
                {t('dialog.sessionTimeout.message')}
              </p>

              {/* Actions — Continue Session primary, Sign Out secondary */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onExtendSession}
                  className="flex-1 min-h-[44px] px-3 py-2 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90 touch-manipulation"
                  style={{
                    background: 'var(--brand-cta-bg)',
                    color: 'var(--vscode-primary-foreground, #fff)',
                    border: 'none',
                    boxShadow: 'var(--brand-cta-shadow)',
                  }}
                >
                  {t('dialog.sessionTimeout.continueSession')}
                </button>
                <button
                  type="button"
                  onClick={onSignOut}
                  className="flex-1 min-h-[44px] px-3 py-2 rounded-lg text-sm font-medium transition-colors touch-manipulation border"
                  style={{
                    background: 'var(--vscode-bg-tertiary)',
                    color: 'var(--vscode-text-secondary)',
                    borderColor: 'var(--vscode-border-primary)',
                  }}
                >
                  {t('dialog.sessionTimeout.signOutNow')}
                </button>
              </div>

              <p className="text-center text-xs opacity-80" style={{ color: 'var(--vscode-text-tertiary)' }}>
                {t('dialog.sessionTimeout.tapOutsideHint')}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default SessionTimeoutModal;
